import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/freshping')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Freshping Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Freshping shut down in March 2026. Uptrack is an alternative: a free plan with 5 monitors at 30-second checks, or 50 monitors for $3/mo on Pro." },
      { property: 'og:title', content: 'Freshping Alternative — Uptrack' },
      { property: 'og:description', content: "Freshping is no longer available. See how Uptrack compares with what Freshping offered: 30s checks, status pages, alert confirmation." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-freshping.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/freshping' },
    ],
    scripts: comparisonScripts('freshping', "Freshping"),
  }),
})

const data: ComparisonData = {
  competitor: "Freshping",
  tagline: "Freshworks stopped new Freshping signups in 2023 and shut the service down in March 2026. If you are moving your monitors, here is how Uptrack compares with what Freshping offered.",
  heroDescription: "Freshworks announced the shutdown to customers by email in January 2026. Free accounts stopped on March 6, 2026, and paid plans did not renew. Freshworks did not name a replacement.",

  features: [
    { feature: "Service status", uptrack: "Active", competitor: "Shut down (March 2026)", uptrackWins: true },
    { feature: "Check interval", uptrack: "30 sec on every plan", competitor: "Was 1 min", uptrackWins: true },
    { feature: "Price", uptrack: "Free for 5 monitors; from $3/mo for 50", competitor: "Was free (50 monitors)" },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "N/A (service closed)", uptrackWins: true },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "N/A (service closed)", uptrackWins: true },
  ],

  painPoints: [
    {
      title: "Freshping is no longer available",
      description: "Freshworks closed Freshping in March 2026. Existing monitors stopped, and the data was scheduled for deletion.",
    },
    {
      title: "Monitors need a new home",
      description: "Freshworks did not name a replacement product, so each monitor must be set up again in another tool. Uptrack Pro holds 50 monitors, the same count as Freshping's free plan.",
    },
    {
      title: "Faster checks than before",
      description: "Freshping checked every minute. Uptrack checks every 30 seconds and alerts only after 2-5 consecutive failed checks.",
    },
  ],

  differentiators: [
    "30-second checks on every plan: Pro is $3/mo for 50 monitors, Team is $20/mo for 200, Business is $79/mo for 1,000.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Status pages on every plan: 1 on free, 5 on Pro, unlimited on Team and Business, with email subscriber notifications on paid plans.",
    "Flat, published pricing: $3, $20, or $79 per month. No per-seat or usage charges, and a 30-day money-back guarantee.",
  ],

  notForYou: [
    "If you need a free replacement with more than 5 monitors, other hosted tools still offer free plans. Uptrack's free plan is 5 monitors at 30 seconds from one location, and paid plans start at $3/mo with a 30-day money-back guarantee.",
    "If you need server metrics (CPU, RAM, disk), Uptrack monitors from outside and does not install agents.",
  ],
}
